import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { StatusBadge } from "./AdminDashboard";
import { Trash2, Eye } from "lucide-react";
import { toast } from "sonner";

const STATUSES = [
  { v: "pending_payment", l: "Paiement en attente" },
  { v: "pending", l: "À traiter" },
  { v: "confirmed", l: "Confirmée" },
  { v: "cancelled", l: "Annulée" },
];

export default function AdminReservations() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const highlightId = params.get("highlight");

  const load = () => {
    api.get("/admin/reservations")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  // Auto-scroll to highlighted reservation and mark as seen
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.querySelector(`[data-row-id="${highlightId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-fegreen", "ring-offset-2");
      setTimeout(() => el.classList.remove("ring-2", "ring-fegreen", "ring-offset-2"), 4000);
    }
    api.post(`/admin/reservations/${highlightId}/mark-seen`).catch(() => {});
  }, [highlightId, loading]);

  const update = async (id, status) => {
    try {
      await api.patch(`/admin/reservations/${id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markSeen = async (id) => {
    try {
      await api.post(`/admin/reservations/${id}/mark-seen`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette réservation ?")) return;
    try {
      await api.delete(`/admin/reservations/${id}`);
      toast.success("Réservation supprimée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const markAllSeen = async () => {
    try {
      const { data } = await api.post("/admin/reservations/mark-all-seen");
      toast.success(`${data.modified} réservation(s) marquée(s) comme lues`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unseenCount = items.filter((r) => r.payment_status === "paid" && !r.admin_seen).length;

  return (
    <div className="p-8 lg:p-10 max-w-7xl">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-10">
        <div>
          <p className="text-fegreen font-medium uppercase tracking-[0.25em] text-xs mb-2">— Gestion</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-fegray-dark">
            Réservations
            {unseenCount > 0 && (
              <span className="ml-3 inline-flex items-center gap-1 bg-fegreen text-white text-xs font-bold px-3 py-1 rounded-full align-middle" data-testid="unseen-count-badge">
                {unseenCount} NOUVELLE{unseenCount > 1 ? "S" : ""}
              </span>
            )}
          </h1>
        </div>
        {unseenCount > 0 && (
          <button
            onClick={markAllSeen}
            data-testid="mark-all-seen-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-fegray-dark hover:text-fegreen border border-gray-200 hover:border-fegreen px-4 py-2 rounded-full transition-colors"
          >
            <Eye className="w-4 h-4" /> Tout marquer comme lu
          </button>
        )}
      </div>

      <div className="fe-card overflow-x-auto">
        {loading ? (
          <div className="p-12 text-center text-fegray">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-fegray">Aucune réservation</div>
        ) : (
          <table className="w-full text-sm" data-testid="admin-reservations-table">
            <thead>
              <tr className="bg-fegray-light text-xs uppercase tracking-wider text-fegray border-b border-gray-100">
                <th className="text-left p-4 w-2">{/* indicator */}</th>
                <th className="text-left p-4">Client</th>
                <th className="text-left p-4">Pack</th>
                <th className="text-left p-4">Date / Heure</th>
                <th className="text-left p-4">Adresse</th>
                <th className="text-left p-4">Montant</th>
                <th className="text-left p-4">Statut</th>
                <th className="text-left p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isUnseen = r.payment_status === "paid" && !r.admin_seen;
                return (
                  <tr
                    key={r.id}
                    data-row-id={r.id}
                    data-testid={`reservation-row-${r.id}`}
                    className={`border-b border-gray-50 transition-colors ${
                      isUnseen
                        ? "bg-fegreen-light/30 hover:bg-fegreen-light/50"
                        : "hover:bg-fegray-light/50"
                    }`}
                  >
                    <td className="p-2 text-center">
                      {isUnseen && (
                        <span className="inline-block w-2.5 h-2.5 bg-fegreen rounded-full pulse-dot" aria-label="Nouvelle" />
                      )}
                    </td>
                    <td className="p-4">
                      <div className="font-semibold text-fegray-dark flex items-center gap-2">
                        {r.prenom} {r.nom}
                        {isUnseen && (
                          <span className="bg-fegreen text-white text-[9px] uppercase font-extrabold tracking-wider px-1.5 py-0.5 rounded">NEW</span>
                        )}
                      </div>
                      <div className="text-xs text-fegray">{r.email}</div>
                      <div className="text-xs text-fegray">{r.telephone}</div>
                    </td>
                    <td className="p-4 text-fegray-dark">{r.pack_name}</td>
                    <td className="p-4 text-fegray-dark">{r.date_souhaitee}<div className="text-xs text-fegray">{r.horaire}</div></td>
                    <td className="p-4 text-xs text-fegray max-w-[200px]">
                      {r.adresse}<br/>{r.code_postal} {r.ville}
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-fegreen">{r.amount_after_credit?.toFixed(2)}€</div>
                    </td>
                    <td className="p-4">
                      <StatusBadge status={r.status} />
                      <div className="mt-1"><StatusBadge status={r.payment_status} /></div>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-2">
                        <select
                          value={r.status}
                          onChange={(e) => update(r.id, e.target.value)}
                          className="bg-white border border-gray-200 rounded-lg px-2 py-1.5 text-xs text-fegray-dark focus:border-fegreen focus:outline-none"
                          data-testid={`status-select-${r.id}`}
                        >
                          {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
                        </select>
                        {isUnseen && (
                          <button
                            onClick={() => markSeen(r.id)}
                            className="text-xs text-fegreen hover:text-fegreen/80 flex items-center gap-1"
                            data-testid={`mark-seen-${r.id}`}
                          >
                            <Eye className="w-3 h-3" /> Marquer comme lue
                          </button>
                        )}
                        <button onClick={() => remove(r.id)} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1">
                          <Trash2 className="w-3 h-3" /> Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
